import { useEffect, useState } from 'react';
import { Header, Card, Button, Badge, useToast } from '../../shared/components';
import { getTurnoGeoRepository } from '../../shared/services/turnoGeo/repositoryFactory';
import { MyNetwork, NetworkReading, ReadingGpsStatus, TurnoGeoSettings } from '../../shared/services/turnoGeo/types';
import { ReadingsTable, StabilityBadge, StabilitySummary, formatReadingTime } from './components/ReadingsTable';

const repo = getTurnoGeoRepository();

type Gps = { status: ReadingGpsStatus | 'REQUESTING'; lat: number | null; lon: number | null; accuracy: number | null };
type Probe = { state: 'NO_CONFIGURADA' | 'CONSULTANDO' | 'OK' | 'SIN_RESPUESTA'; ip: string | null };

async function runProbe(url: string): Promise<Probe> {
  if (!url) return { state: 'NO_CONFIGURADA', ip: null };
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    const res = await fetch(url, { cache: 'no-store', signal: controller.signal });
    clearTimeout(timer);
    const data = (await res.json()) as { ip?: string | null };
    return data.ip ? { state: 'OK', ip: data.ip } : { state: 'SIN_RESPUESTA', ip: null };
  } catch {
    return { state: 'SIN_RESPUESTA', ip: null };
  }
}

/**
 * Registrador: tabla con TODO lo que la aplicación captura de la conexión de
 * la persona en este momento (IPv4, IPv6, proveedor, ciudad, GPS...) y botón
 * "Guardar esta lectura". Las lecturas guardadas muestran si la red es la
 * misma que en la lectura anterior: evidencia de que la información es
 * estable de un día a otro. Solo se muestra lo MEDIDO; no se infiere barrio
 * ni lugar.
 */
export function RegistradorPage() {
  const toast = useToast();
  const [settings, setSettings] = useState<TurnoGeoSettings | null>(null);
  const [net, setNet] = useState<MyNetwork | null>(null);
  const [netError, setNetError] = useState(false);
  const [gps, setGps] = useState<Gps>({ status: 'REQUESTING', lat: null, lon: null, accuracy: null });
  const [p4, setP4] = useState<Probe>({ state: 'CONSULTANDO', ip: null });
  const [p6, setP6] = useState<Probe>({ state: 'CONSULTANDO', ip: null });
  const [history, setHistory] = useState<NetworkReading[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [saved, setSaved] = useState<NetworkReading | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [now, setNow] = useState(new Date());

  const loadHistory = () => {
    setHistoryLoading(true);
    repo.listMyReadings(60).then((r) => {
      setHistory(r);
      setHistoryLoading(false);
    }).catch(() => setHistoryLoading(false));
  };

  const readGps = () => {
    setGps({ status: 'REQUESTING', lat: null, lon: null, accuracy: null });
    if (!('geolocation' in navigator)) {
      setGps({ status: 'NO_DISPONIBLE', lat: null, lon: null, accuracy: null });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ status: 'OK', lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => setGps({ status: err.code === err.PERMISSION_DENIED ? 'DENEGADO' : 'NO_DISPONIBLE', lat: null, lon: null, accuracy: null }),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const refresh = async () => {
    setNetError(false);
    setP4({ state: 'CONSULTANDO', ip: null });
    setP6({ state: 'CONSULTANDO', ip: null });
    setNow(new Date());
    readGps();
    try {
      const [cfg, n] = await Promise.all([repo.getConfiguration(), repo.getMyNetwork()]);
      setSettings(cfg);
      setNet(n);
      const [a, b] = await Promise.all([runProbe(cfg.probeIpv4Url), runProbe(cfg.probeIpv6Url)]);
      setP4(a);
      setP6(b);
    } catch {
      setNetError(true);
    }
  };

  useEffect(() => {
    refresh();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // IPv4 / IPv6 que se muestran: la del servidor + la de la sonda de la otra familia.
  const ipv4 = net?.family === 4 ? net.ip : p4.state === 'OK' ? p4.ip : null;
  const ipv4From = net?.family === 4 ? 'Servidor' : p4.state === 'OK' ? 'Sonda' : null;
  const ipv6 = net?.family === 6 ? net.ip : p6.state === 'OK' ? p6.ip : null;
  const ipv6From = net?.family === 6 ? 'Servidor' : p6.state === 'OK' ? 'Sonda' : null;
  const missing = (family: 4 | 6, probe: Probe) => {
    if (net?.family === family) return null;
    if (probe.state === 'CONSULTANDO') return 'Consultando…';
    if (probe.state === 'NO_CONFIGURADA') return `No disponible: tu conexión llegó por IPv${net?.family ?? '?'} y la sonda IPv${family} no está configurada`;
    if (probe.state === 'SIN_RESPUESTA') return `Sin respuesta de la sonda IPv${family} (la conexión puede no tener IPv${family})`;
    return null;
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const result = await repo.saveReading({
        ipv4Probe: p4.state === 'OK' ? p4.ip : null,
        ipv6Probe: p6.state === 'OK' ? p6.ip : null,
        gpsLatitude: gps.lat,
        gpsLongitude: gps.lon,
        gpsAccuracyMeters: gps.accuracy,
        gpsStatus: gps.status === 'REQUESTING' ? 'NO_DISPONIBLE' : gps.status,
        clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setSaved(result);
      toast.show('Lectura guardada.');
      loadHistory();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar la lectura.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const gpsText =
    gps.status === 'OK'
      ? `${gps.lat!.toFixed(6)}, ${gps.lon!.toFixed(6)} (±${Math.round(gps.accuracy ?? 0)} m)`
      : gps.status === 'DENEGADO'
        ? 'Permiso denegado por la persona'
        : gps.status === 'REQUESTING'
          ? 'Solicitando…'
          : 'No disponible en este dispositivo';

  const rows: { label: string; value: React.ReactNode; note?: string }[] = [
    { label: 'Fecha y hora (Colombia)', value: now.toLocaleString('es-CO', { timeZone: settings?.timeZone ?? 'America/Bogota', dateStyle: 'full', timeStyle: 'medium' }) },
    { label: 'IP que ve el servidor', value: net ? net.ip ?? 'No detectada' : netError ? 'Error al consultar' : 'Consultando…', note: net?.family ? `Llegó por IPv${net.family}` : undefined },
    { label: 'IPv4', value: ipv4 ?? missing(4, p4) ?? '—', note: ipv4From ? `Fuente: ${ipv4From}` : undefined },
    { label: 'IPv6', value: ipv6 ?? missing(6, p6) ?? '—', note: ipv6From ? `Fuente: ${ipv6From}` : undefined },
    { label: 'Se compara como', value: net?.comparedAs ?? '—', note: net?.family === 6 ? `Prefijo de red de ${net.prefixLength} bits (la parte final de la IPv6 rota sola)` : undefined },
    {
      label: 'Proveedor de internet',
      value: net?.network.provider ?? '—',
      note: net?.network.providerRegistered ? `Nombre en el registro de redes: ${net.network.providerRegistered}${net.network.asn ? ` (AS${net.network.asn})` : ''}` : undefined,
    },
    { label: 'GPS del dispositivo', value: gpsText },
    { label: 'Zona horaria del dispositivo', value: Intl.DateTimeFormat().resolvedOptions().timeZone },
    {
      label: 'Coincide con',
      value: net ? (
        <div className="flex flex-wrap gap-2">
          {net.matchesOffice && <Badge tone="success">Sede: {net.matchesOffice.name}</Badge>}
          {net.ownOffice?.matches && <Badge tone={net.ownOffice.status === 'APROBADA' ? 'success' : 'warning'}>Tu oficina ({net.ownOffice.status.toLowerCase()})</Badge>}
          {net.home?.matches && <Badge tone={net.home.status === 'APROBADA' ? 'success' : 'warning'}>Tu casa ({net.home.status.toLowerCase()})</Badge>}
          {!net.matchesOffice && !net.ownOffice?.matches && !net.home?.matches && <Badge tone="neutral">Ninguna red registrada</Badge>}
        </div>
      ) : (
        '—'
      ),
    },
  ];

  return (
    <div>
      <Header title="Registrador" subtitle="TurnoGeo" />
      <div className="space-y-4 p-4">
        <Card>
          <p className="text-sm font-semibold text-ink-800">Esto es lo que la aplicación captura de tu conexión ahora</p>
          <dl className="mt-3 divide-y divide-ink-100">
            {rows.map((row) => (
              <div key={row.label} className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-3 sm:gap-3">
                <dt className="text-xs font-semibold uppercase tracking-wide text-ink-400">{row.label}</dt>
                <dd className="break-all text-sm text-ink-800 sm:col-span-2">
                  {row.value}
                  {row.note && <span className="mt-0.5 block text-xs text-ink-400">{row.note}</span>}
                </dd>
              </div>
            ))}
          </dl>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={refresh}>Volver a leer</Button>
            <Button onClick={save} isLoading={isSaving} disabled={!net?.ip}>Guardar esta lectura</Button>
          </div>
          <p className="mt-3 text-xs text-ink-400">
            Se guarda exactamente lo de la tabla, con fecha y hora. No se calcula barrio ni lugar: solo lo medido. Guarda una lectura cada día para comprobar que tu red se mantiene igual.
          </p>
        </Card>

        {saved && (
          <Card>
            <p className="text-sm font-semibold text-ink-800">Lectura guardada · {formatReadingTime(saved.readingTimestamp)}</p>
            <div className="mt-2"><StabilityBadge r={saved} /></div>
          </Card>
        )}

        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-400">Mis lecturas guardadas</h3>
          <StabilitySummary rows={history} />
          <ReadingsTable rows={history} isLoading={historyLoading} />
        </section>
      </div>
    </div>
  );
}
